import { useRef, useState } from 'react'
import { Form, Link, useRouteLoaderData, useSubmit } from 'react-router'
import { categoryPlural } from '#app/utils/category-name.ts'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet.tsx'
import { type loader } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { pluralize } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { useMarketingNav } from '../_layout.tsx'

export function MarketingHeader() {
	const user = useOptionalUser()
	const data = useRouteLoaderData<typeof loader>('root')
	const nav = useMarketingNav()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const rankings = nav?.rankings ?? []
	const popularPairs = nav?.popularPairs ?? []
	const hasComparisons = (nav?.comparisonCount ?? 0) > 0

	return (
		<>
			{/*
			 * Three zones: hubs left, search centred, actions right.
			 *
			 * The side tracks are equal `1fr`, so the search bar is centred on
			 * the viewport rather than on whatever space the sides leave over.
			 * That matters because both sides change width at runtime: the
			 * Compare link is conditional, and the signed-in actions are wider
			 * than the signed-out ones. Sizing the centre off the sides would
			 * let the search bar jump sideways as those swap in and out.
			 *
			 * The centre track is `auto` and the form carries its own width, so
			 * the track is genuinely that wide. A `minmax(0,Xrem)` track would
			 * not be: with no `fr` of its own it never absorbs free space, the
			 * `1fr` sides take it all, and the track collapses to the input's
			 * intrinsic width.
			 *
			 * `min-w-0` on the sides is the safety valve. If a side ever
			 * outgrows its track it truncates there instead of shoving the
			 * search bar off centre.
			 *
			 * Below `md` the search form is hidden, so there is no centre to
			 * hold and the grid becomes actively harmful: the empty middle
			 * track collapses and the two `1fr` sides split the viewport in
			 * half, which is not enough room for the hamburger and the logo.
			 * So the grid starts at the same breakpoint the search bar does,
			 * and narrower than that the header is a plain
			 * `justify-between` flex row.
			 */}
			<header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 fixed inset-x-0 top-0 z-50 h-16 shrink-0 border-b backdrop-blur">
				<div className="flex h-16 items-center justify-between gap-2 px-4 md:grid md:grid-cols-[1fr_auto_1fr]">
					<div className="flex min-w-0 items-center gap-2">
						{/* Mobile Menu Trigger */}
						<Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
							<SheetTrigger asChild>
								<Button variant="ghost" size="icon" className="lg:hidden">
									<Icon name="hamburger-menu" />
									<span className="sr-only">Toggle menu</span>
								</Button>
							</SheetTrigger>
							<SheetContent side="left" className="overflow-y-auto">
								<SheetHeader>
									<SheetTitle>Menu</SheetTitle>
								</SheetHeader>
								<nav className="mt-8 flex flex-col gap-4 px-4 pb-8">
									{popularPairs.length > 0 ? (
										<>
											<p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
												Popular
											</p>
											{popularPairs.map((pair) => (
												<Link
													key={`${pair.citySlug}/${pair.categorySlug}`}
													to={`/${pair.citySlug}/${pair.categorySlug}`}
													className="text-foreground hover:text-primary font-medium transition-colors"
													onClick={() => setMobileMenuOpen(false)}
												>
													{categoryPlural({ name: pair.categoryName, pluralName: pair.categoryPluralName })} in{' '}
															{pair.cityName}
												</Link>
											))}
											<hr className="my-1" />
										</>
									) : null}

									{rankings.length > 0 ? (
										<>
											<p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
												Rankings
											</p>
											{rankings.map((category) => (
												<Link
													key={category.slug}
													to={`/best/${category.slug}`}
													className="text-foreground hover:text-primary font-medium transition-colors"
													onClick={() => setMobileMenuOpen(false)}
												>
													Best {categoryPlural(category)} in DFW
												</Link>
											))}
											<hr className="my-1" />
										</>
									) : null}

									<Link
										to="/categories"
										className="text-foreground hover:text-primary text-lg font-medium transition-colors"
										onClick={() => setMobileMenuOpen(false)}
									>
										Categories
									</Link>
									<Link
										to="/cities"
										className="text-foreground hover:text-primary text-lg font-medium transition-colors"
										onClick={() => setMobileMenuOpen(false)}
									>
										Cities
									</Link>
									{hasComparisons ? (
										<Link
											to="/compare"
											className="text-foreground hover:text-primary text-lg font-medium transition-colors"
											onClick={() => setMobileMenuOpen(false)}
										>
											Compare
										</Link>
									) : null}
									<Link
										to="/scoring-method"
										className="text-foreground hover:text-primary text-lg font-medium transition-colors"
										onClick={() => setMobileMenuOpen(false)}
									>
										How we score
									</Link>

									<hr className="my-2" />
									{user ? (
										<>
											<Link
												to="/manage"
												className="text-foreground hover:text-primary text-lg font-medium transition-colors"
												onClick={() => setMobileMenuOpen(false)}
											>
												Manage Listings
											</Link>
											<Link
												to="/profile"
												className="text-foreground hover:text-primary text-lg font-medium transition-colors"
												onClick={() => setMobileMenuOpen(false)}
											>
												Profile
											</Link>
											{user?.roles.some((role) => role.name === 'ADMIN') && (
												<Link
													to="/dashboard"
													className="text-foreground hover:text-primary text-lg font-medium transition-colors"
													onClick={() => setMobileMenuOpen(false)}
												>
													Dashboard
												</Link>
											)}
										</>
									) : (
										<>
											<Link
												to="/login?redirectTo=/manage"
												className="text-foreground hover:text-primary text-lg font-medium transition-colors"
												onClick={() => setMobileMenuOpen(false)}
											>
												Log In
											</Link>
											<Link
												to="/submit"
												className="text-foreground hover:text-primary text-lg font-medium transition-colors"
												onClick={() => setMobileMenuOpen(false)}
											>
												Submit Business
											</Link>
										</>
									)}
								</nav>
							</SheetContent>
						</Sheet>

						{/*
						 * Stacked so the full name fits the width the header was laid
						 * out for: inline, it pushes into the centered search at md and
						 * past the icon buttons at 320px.
						 */}
						<Link
							className="flex shrink-0 cursor-pointer items-center gap-2 font-serif font-bold tracking-tight"
							to="/"
							aria-label="DFW Professionals"
						>
							<div className="bg-accent hidden h-8 w-8 items-center justify-center rounded-sm font-serif text-xl text-white sm:flex">
								D
							</div>
							<span className="flex flex-col text-base leading-none">
								<span>DFW</span>
								<span>Professionals</span>
							</span>
						</Link>

						{/*
						 * Flat links, deliberately not dropdowns.
						 *
						 * Radix menu content is not server-rendered, so every link that
						 * lived inside the old Browse and Rankings menus was invisible to
						 * crawlers and to models: the header shipped zero /best/ and zero
						 * city x category links while appearing to be the site's main
						 * navigation. It was carrying the complexity of a menu and buying
						 * nothing for discovery.
						 *
						 * It also could not scale. At ~106 categories a menu has to
						 * truncate, and the cut is invisible to the reader. Fan-out
						 * belongs on the hub pages, which are crawlable and can group,
						 * paginate and filter; sitewide chrome carries only the hubs and a
						 * small stable set of leaves, so link equity concentrates instead
						 * of spreading evenly across the whole catalog.
						 */}
						{/*
						 * Only the hubs. `How we score` used to sit here too, but
						 * /scoring-method already earns thirteen inbound links from the
						 * footer and inline from every ranking, city, compare and listing
						 * page — beside the claim it actually backs. A fourteenth link in
						 * the chrome bought nothing and cost the width the search bar needs.
						 */}
						<nav className="ml-6 hidden items-center gap-1 lg:flex">
							<Button variant="ghost" size="sm" asChild className="font-medium">
								<Link to="/categories">Categories</Link>
							</Button>
							<Button variant="ghost" size="sm" asChild className="font-medium">
								<Link to="/cities">Cities</Link>
							</Button>
							{hasComparisons ? (
								<Button
									variant="ghost"
									size="sm"
									asChild
									className="font-medium"
								>
									<Link to="/compare">Compare</Link>
								</Button>
							) : null}
						</nav>
					</div>

					{/*
					 * Search is what replaces the menus. At 106 categories across 55
					 * cities the catalog is a search problem, not a menu problem, and
					 * a plain GET form works without JavaScript.
					 *
					 * It appears a breakpoint earlier than the hub links, because most
					 * sessions land deep from a search engine or an AI answer and search
					 * is how they move sideways. When width is scarce, search wins and
					 * the hubs fall back to the sheet.
					 */}
					<Form
						action="/search"
						method="GET"
						role="search"
						className="hidden w-64 md:block lg:w-72 xl:w-96"
					>
						<label htmlFor="site-search" className="sr-only">
							Search businesses, categories and cities
						</label>
						<div className="relative">
							<Icon
								name="magnifying-glass"
								className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
							/>
							<input
								id="site-search"
								type="search"
								name="q"
								placeholder="Search businesses, categories, cities"
								className="border-input bg-background focus-visible:ring-ring h-9 w-full rounded-md border py-1 pr-3 pl-8 text-sm focus-visible:ring-1 focus-visible:outline-none"
							/>
						</div>
					</Form>

					{/* Right side actions */}
					<div className="flex min-w-0 items-center justify-end gap-2">
						<Button
							variant="ghost"
							size="icon"
							asChild
							className="md:hidden"
							aria-label="Search"
						>
							<Link to="/search">
								<Icon name="magnifying-glass" className="size-4" />
							</Link>
						</Button>

						<ThemeSwitch userPreference={data?.requestInfo.userPrefs.theme} />

						<div className="hidden min-w-0 items-center gap-2 md:flex">
							{user ? (
								<>
									<Button asChild size="sm">
										<Link to="/manage">Manage Listings</Link>
									</Button>
									<UserDropdown />
								</>
							) : (
								<>
									<Button asChild variant="ghost" size="sm">
										<Link to="/login?redirectTo=/manage">Log In</Link>
									</Button>
									<Button asChild size="sm">
										<Link to="/submit">Submit Business</Link>
									</Button>
								</>
							)}
						</div>
					</div>
				</div>
			</header>
			<div aria-hidden className="h-16" />
		</>
	)
}

function UserDropdown() {
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	const user = useOptionalUser()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="sm">
					<Icon name="user" />
					{/* Capped: an unbounded name here would eat the search track. */}
					<span className="max-w-[10ch] truncate max-sm:hidden">
						{user?.name}
					</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={1} align="end">
					{user?.roles.some((role) => role.name === 'ADMIN') && (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to={`/dashboard`}>
								<Icon name="layout-dashboard" size="xs">
									Dashboard
								</Icon>
							</Link>
						</DropdownMenuItem>
					)}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/profile`}>
							<Icon name="user" size="xs">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={(event) => {
							event.preventDefault()
							void submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon name="exit" size="xs">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
